import type { FC } from "react";
import { useCallback, useEffect, useState } from "react";

import { getErrorMessageFromError } from "@jti/fe-common";
import {
  useUpdateLoyaltyReferralCodeMutation,
  useVerifyLoyaltyReferralCodeMutation,
} from "@jti/store/slices/loyaltyApiSlice";
import { dispatchTrackingEvent, TrackingEvents } from "@jti/tracker";
import { useTranslations } from "@jti/ui";

import { useCheckoutV2Configuration } from "@checkout-v2/shared/providers/CheckoutV2ConfigurationProvider/hooks/useCheckoutV2Configuration";

import { useReferralCustomer } from "./hooks";
import { LoyaltyReferralCodeView } from "./LoyaltyReferralCodeView";

export const LoyaltyReferralCodeContainer: FC = () => {
  const verifyCodeErrorKey = "cart.loyalty.referral.form.validation.verifyCode";
  const { loyalty } = useCheckoutV2Configuration();
  const { dictionary } = useTranslations();
  const isFeatureEnabled = Boolean(loyalty?.showMgm);

  const {
    parentCode,
    hasFirstPurchase,
    isUserEligible,
    hasCustomerData,
    isLoading: queryLoading,
    user,
  } = useReferralCustomer({ skip: !isFeatureEnabled });

  const [verifyCode, verifyState] = useVerifyLoyaltyReferralCodeMutation();
  const [updateReferralCode, updateState] = useUpdateLoyaltyReferralCodeMutation();

  const isLoading = queryLoading || verifyState.isLoading || updateState.isLoading;

  const [errorMessages, setErrorMessages] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState<boolean>(true);

  const resetErrorMessage = useCallback(() => {
    setErrorMessages(null);
  }, []);

  const verifyCodeErrorMessage = getErrorMessageFromError(
    { error_code: verifyCodeErrorKey },
    verifyCodeErrorKey,
    dictionary
  );

  const onSubmit = async (code: string) => {
    const trimmedCode = (code || "").trim();
    if (!trimmedCode) {
      setErrorMessages(verifyCodeErrorMessage);
      return;
    }

    try {
      const verifyResult = await verifyCode({
        referralCode: trimmedCode,
        ciam_token: user.ciamToken,
      }).unwrap();

      if (!verifyResult) {
        setErrorMessages(verifyCodeErrorMessage);
        return;
      }

      const applyResult = await updateReferralCode({
        referralCode: trimmedCode,
        uuid: user.uuid,
        source: "checkout",
      }).unwrap();

      if (!applyResult) {
        setErrorMessages(verifyCodeErrorMessage);
        return;
      }

      dispatchTrackingEvent(TrackingEvents.TrackingReferralCodeInserted);
      setErrorMessages(null);
    } catch (err: unknown) {
      const message = getErrorMessageFromError(err, verifyCodeErrorKey, dictionary);
      setErrorMessages(message || verifyCodeErrorMessage);
    }
  };

  useEffect(() => {
    if (parentCode) setCollapsed(false);
  }, [parentCode]);

  const shouldShowCard = hasCustomerData && isUserEligible && !hasFirstPurchase;

  if (!shouldShowCard) {
    return null;
  }

  return (
    <LoyaltyReferralCodeView
      referralCode={parentCode}
      isSuccess={Boolean(parentCode)}
      isLoading={isLoading}
      error={errorMessages}
      onSubmit={onSubmit}
      onFocusInput={resetErrorMessage}
      collapsed={collapsed}
    />
  );
};
